/**
 * Listing Categories — the industry list every listing is filed under.
 *
 * TKT-00124: advisors reported the category list was too limited, so businesses
 * that fit nowhere end up in the catch-all. The list was seed data with no
 * screen behind it, so growing it meant hand-written SQL. This is that screen.
 *
 * Two rules the API enforces, mirrored here so they don't surprise anyone:
 *  - A category in use cannot be deleted. industry_category_id is
 *    ON DELETE SET NULL, so deleting would quietly strip the category from those
 *    listings. Deactivating is the move: it leaves the pickers, listings keep it.
 *  - Names are unique case-insensitively — "Auto Repair" and "auto repair" are
 *    the same thing to anyone reading a dropdown.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Ban, Check, Loader2, Pencil, Plus, Tags, Trash2, X } from "lucide-react";

import { PageHero } from "@/components/interior-page";

import { superAdminApi } from "./session";

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  parent_id: number | null;
  is_active: boolean;
  sort_order: number;
  sde_multiple_low: number | null;
  sde_multiple_high: number | null;
  ebitda_multiple_low: number | null;
  ebitda_multiple_high: number | null;
  listing_count: number;
}

const MULTIPLE_KEYS = ["sde_multiple_low", "sde_multiple_high", "ebitda_multiple_low", "ebitda_multiple_high"] as const;
type MultipleKey = (typeof MULTIPLE_KEYS)[number];

/** "2.5–3.5x", or a dash when the category has no Valuation Calculator preset. */
const rangeText = (low: number | null, high: number | null) => (low != null && high != null ? `${low}–${high}x` : "—");

type AddForm = { name: string; description: string };
type EditForm = { name: string; description: string; sort_order: string } & Record<MultipleKey, string>;
const EMPTY_EDIT: EditForm = {
  name: "", description: "", sort_order: "",
  sde_multiple_low: "", sde_multiple_high: "", ebitda_multiple_low: "", ebitda_multiple_high: "",
};

export default function SuperAdminListingCategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState<AddForm | null>(null);
  const [saving, setSaving] = useState(false);
  // Id of the row mid-request, so only its own control spins.
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [edit, setEdit] = useState<EditForm>(EMPTY_EDIT);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCategories(await superAdminApi.get<Category[]>("/listing-categories"));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load listing categories.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // The reason this module exists: how much of the inventory has piled into the
  // single largest category. A catch-all holding a third of everything is the
  // signal that a real category is missing.
  const concentration = useMemo(() => {
    const total = categories.reduce((sum, c) => sum + c.listing_count, 0);
    if (!total) return null;
    const biggest = categories.reduce((a, b) => (b.listing_count > a.listing_count ? b : a));
    return { total, biggest, share: Math.round((biggest.listing_count / total) * 100) };
  }, [categories]);

  const add = async () => {
    if (!adding || saving) return;
    const name = adding.name.trim();
    if (name.length < 2) {
      setError("A category name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await superAdminApi.post<Category>("/listing-categories", {
        name,
        description: adding.description.trim() || null,
      });
      setAdding(null);
      setNotice(`"${name}" is now available when creating or editing a listing.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add the category.");
    } finally {
      setSaving(false);
    }
  };

  const patch = async (c: Category, body: Record<string, unknown>, message?: string) => {
    setBusyId(c.id);
    setError(null);
    setNotice(null);
    try {
      await superAdminApi.patch<Category>(`/listing-categories/${c.id}`, body);
      setEditingId(null);
      if (message) setNotice(message);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update the category.");
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async (c: Category) => {
    const name = edit.name.trim();
    if (name.length < 2) {
      setError("A category name is required.");
      return;
    }
    const sortOrder = Number(edit.sort_order);
    // Valuation Calculator presets: blank clears; the API rejects a half pair.
    const multiples: Record<string, number | null> = {};
    for (const k of MULTIPLE_KEYS) {
      const raw = edit[k].trim();
      const n = Number(raw);
      if (raw !== "" && (!Number.isFinite(n) || n <= 0 || n > 20)) {
        setError("Multiples must be numbers above 0 and at most 20.");
        return;
      }
      multiples[k] = raw === "" ? null : n;
    }
    await patch(
      c,
      {
        name,
        description: edit.description.trim() || null,
        ...(Number.isFinite(sortOrder) && edit.sort_order !== "" ? { sort_order: sortOrder } : {}),
        ...multiples,
      },
      `"${name}" was updated.`,
    );
  };

  const remove = async (c: Category) => {
    if (!window.confirm(
      `Delete "${c.name}"?\n\nThis removes the category from the platform entirely. ` +
      `It is only possible because no listing is using it.`,
    )) return;
    setBusyId(c.id);
    setError(null);
    setNotice(null);
    try {
      await superAdminApi.delete(`/listing-categories/${c.id}`);
      setNotice(`"${c.name}" was deleted.`);
      await load();
    } catch (e) {
      // The in-use refusal arrives here with the listing count and the advice to
      // deactivate instead — worth showing verbatim rather than a generic line.
      setError(e instanceof Error ? e.message : "Could not delete the category.");
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (c: Category) => {
    setError(null);
    setNotice(null);
    setEditingId(c.id);
    setEdit({
      name: c.name,
      description: c.description ?? "",
      sort_order: String(c.sort_order),
      ...(Object.fromEntries(MULTIPLE_KEYS.map((k) => [k, c[k] == null ? "" : String(c[k])])) as Record<MultipleKey, string>),
    });
  };

  return (
    <main className="w-full space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <PageHero
        eyebrow="Platform"
        icon={Tags}
        title="Listing"
        titleAccent="Categories"
        description="The industry list every listing is filed under, and every category filter reads from. Adding one here makes it selectable on the create and edit listing forms straight away — no redeploy, no SQL."
      />

      {error && (
        <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-600">
          {error}
        </p>
      )}
      {notice && (
        <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm font-medium text-emerald-600">
          {notice}
        </p>
      )}

      {/* Why this screen exists, in one line of live data. */}
      {concentration && concentration.share >= 15 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700">
          <span className="font-semibold">{concentration.biggest.name}</span> holds{" "}
          <span className="font-semibold tabular-nums">{concentration.biggest.listing_count.toLocaleString()}</span> of{" "}
          <span className="tabular-nums">{concentration.total.toLocaleString()}</span> listings ({concentration.share}%).
          A category that large is usually a sign a more specific one is missing.
        </div>
      )}

      <div className="rounded-2xl border border-border/80 bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-5 py-4">
          <div>
            <h2 className="text-base font-bold text-foreground">
              {categories.length} categor{categories.length === 1 ? "y" : "ies"}
            </h2>
            <p className="text-xs text-muted-foreground">
              Inactive ones stay on their existing listings but disappear from the pickers.
            </p>
          </div>
          {!adding && (
            <button
              type="button"
              onClick={() => { setAdding({ name: "", description: "" }); setError(null); setNotice(null); }}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gold-gradient px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
            >
              <Plus className="h-4 w-4" /> Add category
            </button>
          )}
        </div>

        {adding && (
          <div className="space-y-3 border-b border-border/70 bg-muted/30 px-5 py-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Name *
                </label>
                <input
                  autoFocus
                  value={adding.name}
                  onChange={(e) => setAdding({ ...adding, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
                  placeholder="e.g. Auto Repair"
                  className="h-10 w-full rounded-xl border border-border/80 bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Description
                </label>
                <input
                  value={adding.description}
                  onChange={(e) => setAdding({ ...adding, description: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") void add(); }}
                  placeholder="Optional — internal note on what belongs here"
                  className="h-10 w-full rounded-xl border border-border/80 bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              The URL slug is generated from the name, and the category is added to the end of the list.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void add()}
                disabled={saving}
                className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gold-gradient px-4 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Save category
              </button>
              <button
                type="button"
                onClick={() => { setAdding(null); setError(null); }}
                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border/80 px-4 text-sm font-semibold text-muted-foreground hover:bg-muted"
              >
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 px-5 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : categories.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <Tags className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-3 font-semibold text-foreground">No categories yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Add the first one to make it selectable on a listing.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  {["Category", "Slug", "Listings", "SDE multiple", "EBITDA multiple", "Order", "Status", ""].map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap px-4 py-3 text-left text-[0.65rem] font-bold uppercase tracking-[0.16em] text-muted-foreground"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {categories.map((c) => {
                  const busy = busyId === c.id;
                  const isEditing = editingId === c.id;
                  return (
                    <tr key={c.id} className={`transition-colors hover:bg-muted/20 ${c.is_active ? "" : "opacity-60"}`}>
                      {isEditing ? (
                        <td colSpan={8} className="px-4 py-3">
                          <div className="grid gap-3 sm:grid-cols-[2fr_3fr_auto]">
                            <input
                              autoFocus
                              value={edit.name}
                              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                              className="h-9 rounded-xl border border-border/80 bg-background px-3 text-sm"
                              placeholder="Name"
                            />
                            <input
                              value={edit.description}
                              onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                              className="h-9 rounded-xl border border-border/80 bg-background px-3 text-sm"
                              placeholder="Description"
                            />
                            <input
                              value={edit.sort_order}
                              onChange={(e) => setEdit({ ...edit, sort_order: e.target.value.replace(/\D/g, "") })}
                              className="h-9 w-24 rounded-xl border border-border/80 bg-background px-3 text-sm tabular-nums"
                              placeholder="Order"
                              inputMode="numeric"
                            />
                          </div>
                          {/* Presets the buyer Valuation Calculator offers for this industry. */}
                          <div className="mt-3 grid gap-3 sm:grid-cols-4">
                            {([
                              ["sde_multiple_low", "SDE low"],
                              ["sde_multiple_high", "SDE high"],
                              ["ebitda_multiple_low", "EBITDA low"],
                              ["ebitda_multiple_high", "EBITDA high"],
                            ] as const).map(([k, label]) => (
                              <label key={k} className="space-y-1">
                                <span className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label} ×</span>
                                <input
                                  value={edit[k]}
                                  onChange={(e) => setEdit({ ...edit, [k]: e.target.value.replace(/[^\d.]/g, "") })}
                                  className="h-9 w-full rounded-xl border border-border/80 bg-background px-3 text-sm tabular-nums"
                                  placeholder="—"
                                  inputMode="decimal"
                                />
                              </label>
                            ))}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Valuation Calculator presets. Set both of a pair or leave both blank.
                          </p>
                          <div className="mt-3 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => void saveEdit(c)}
                              disabled={busy}
                              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-gold-gradient px-3 text-xs font-semibold text-primary-foreground disabled:opacity-50"
                            >
                              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border/80 px-3 text-xs font-semibold text-muted-foreground hover:bg-muted"
                            >
                              <X className="h-3.5 w-3.5" /> Cancel
                            </button>
                            {/* The slug is deliberately not editable: it is the
                                category's stable identifier, and renaming a
                                label shouldn't move it. */}
                            <span className="text-xs text-muted-foreground">Slug stays {c.slug}</span>
                          </div>
                        </td>
                      ) : (
                        <>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-foreground">{c.name}</span>
                            {c.description && (
                              <span className="mt-0.5 block text-xs text-muted-foreground">{c.description}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.slug}</td>
                          <td className="px-4 py-3">
                            <span className="font-semibold tabular-nums text-foreground">
                              {c.listing_count.toLocaleString()}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums text-muted-foreground">
                            {rangeText(c.sde_multiple_low, c.sde_multiple_high)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 tabular-nums text-muted-foreground">
                            {rangeText(c.ebitda_multiple_low, c.ebitda_multiple_high)}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-muted-foreground">{c.sort_order}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-wide ${
                                c.is_active
                                  ? "bg-emerald-500/10 text-emerald-600"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {c.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => startEdit(c)}
                                title="Rename, reorder or set multiples"
                                className="flex h-7 w-7 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => void patch(
                                  c,
                                  { is_active: !c.is_active },
                                  c.is_active
                                    ? `"${c.name}" is hidden from the pickers. Its ${c.listing_count} listing${c.listing_count === 1 ? "" : "s"} keep it.`
                                    : `"${c.name}" is selectable again.`,
                                )}
                                title={c.is_active ? "Deactivate — hides it from the pickers" : "Reactivate"}
                                className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted disabled:opacity-50"
                              >
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : c.is_active ? <Ban className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                              </button>
                              <button
                                type="button"
                                disabled={busy || c.listing_count > 0}
                                onClick={() => void remove(c)}
                                title={
                                  c.listing_count > 0
                                    ? `In use by ${c.listing_count} listing${c.listing_count === 1 ? "" : "s"} — deactivate it instead`
                                    : "Delete"
                                }
                                className="flex h-7 w-7 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-30"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
